import React, { FC, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useMarket } from "../../api/hooks/useMarket";
import { useAppDispatch, useAppSelector } from "../hooks";
import { TokenData, TokenDataNew } from "../user/types";
import {
  selectSolAccount,
  selectEVMAccount,
  setEVMAccount,
  setChange,
  setWallet,
  setTokens,
  selectTokens,
} from "./userSlice";
import { selectIsConnected, setIsConnected } from "../app/appSlice";
import { useCovalent } from "../../api/hooks/useCovalent";
import { blacklist } from "../../constants/tokenBlacklist";
import { ethers } from "ethers";
import {
  selectZoom,
  setChanges,
  setData,
  setDayData,
  setHistData,
  setPnl,
} from "../chart/chartSlice";
import { selectIDs } from "../market/marketSlice";
import { findDates } from "../chart/chartDates";
import { ChartData, HistTokenData } from "../chart/types";
import { useZoom } from "./hooks/useZoom";
import { useMakeChartData } from "../chart/useMakeChartData";
import { CovalentHistReturn } from "../market/types";

//chains are pre-determined. To start they are:
//ethereum, binance, polygon, aave
const chains = ["1", "56", "137", "43114"];

type covalentTokenResponse = {
  filteredItems: any[];
  chain_id: string;
}

const UserUpdater: FC = (): null => {
  const dispatch = useAppDispatch();
  const covalentFunc = useCovalent();
  const [tokenData, setTokenData] = useState<TokenDataNew[]>([]);
  const [dateData, setDateData] = useState<string[]>([]);
  const evmAcct = useAppSelector(selectEVMAccount);
  const solAcct = "9itTk7yRYhWEJcF6Yudm9NL7icktEdhm88teyMSAUtBK"//useAppSelector(selectSolAccount);
  const makeChartDataFuncs = useMakeChartData();
  const zoomFuncs = useZoom();
  const zoom = useAppSelector(selectZoom);

  
  const handleHistorical = (
    tokens: TokenDataNew[],
    evmWallet?: string,
    solWallet?: string
  ) => {
    let promises: any[] = [];
    let storeChartData = {} as ChartData;


    //create promises if wallets are connected
    if (evmWallet)
      chains.forEach(async (chainID) => {
        promises.push(covalentFunc.getHistoricalValue(evmWallet, chainID));
      });


    if (solAcct) promises.push(covalentFunc.getHistoricalValueSol(solAcct));

    //execute all promises
    Promise.all(promises).then((response) => {
      let merged: any[] = [].concat.apply([], response);
      let recalibrated: any[] = merged.map((data) => {
        //console.log(data)
        return data.data.items
      });
      let newMerged: any[] = [].concat.apply([], recalibrated);
      //console.log(newMerged)
      const filteredList = newMerged.filter((token: CovalentHistReturn) => {
        //filters out small holdings and blacklisted tokens
        //*** CAREFUL THIS REMOVES ANY TOKEN THAT HAS A 0 VALUE ON THE CURRENT DAY ***
        //*** BETTER SOLUTION REQUIRED, CHECK PREVIOUS DATES FOR VALUE ***
        if (token) {
          if (
            !blacklist.includes(token.contract_name) &&
            token.holdings[0].close.quote > 0.01
          ) {
            return true;
          }
          return false;
        }
      });

      //day data
       makeChartDataFuncs.handleDay(tokens).then((response) => dispatch(setDayData(response)));


      //week data
      const weekChartData = makeChartDataFuncs.handleWeek(
        filteredList,
        storeChartData
      );
      storeChartData.weekAccumPnl = weekChartData.weekAccumPnl;
      storeChartData.weekDates = weekChartData.weekDates;
      storeChartData.weekPnl = weekChartData.weekPnl;
      storeChartData.weekValues = weekChartData.weekValues;
      storeChartData.weekChanges = weekChartData.weekChanges;

      //month data
      const monthChartData = makeChartDataFuncs.handleMonth(
        filteredList,
        storeChartData
      );

      storeChartData.monthAccumPnl = monthChartData.monthAccumPnl;
      storeChartData.monthDates = monthChartData.monthDates;
      storeChartData.monthPnl = monthChartData.monthPnl;
      storeChartData.monthValues = monthChartData.monthValues;
      storeChartData.monthChanges = monthChartData.monthChanges;
      //sets data in redux
      dispatch(setData(storeChartData));
      // setDateData(dates);

      dispatch(
        setChange(
          storeChartData.weekChanges[storeChartData.weekChanges.length - 1]
        )
      );
      
    });
    return storeChartData;
  };

  //grabs the user's token balances from Covalent
  //and updates redux with it
  const handleCovalent = async (evmWallet?: string) => {
    let promises: Promise<covalentTokenResponse>[] = [];

    if (evmWallet)
      chains.forEach(async (chainID) => {
        promises.push(covalentFunc.getTokens(evmWallet, chainID));
      });

    if (solAcct) promises.push(covalentFunc.getSolTokens(solAcct))


    Promise.all(promises).then((response) => {
      let tokenResp: covalentTokenResponse[] = [];
      
      let merged: any[] = tokenResp.concat.apply([], response);

      let newTokens: TokenDataNew[] = [];
      //quote = current value of held tokens
      //quote_24h = value of held tokens 24h earlier
      //quote_rate = current price of token on market
      //quote_rate_24h = price of token on market 24h prior
      merged.forEach((chain) => {
        chain.filteredItems.forEach((token: any) => {
        if (token && !blacklist.includes(token.contract_name)) {
          newTokens.push({
            token: token.contract_name,
            symbol: token.contract_ticker_symbol,
            balance: token.balance / 10 ** token.contract_decimals,
            value: token.quote,
            apy: 0,
            change24:
              ((token.quote_rate - token.quote_rate_24h) / token.quote_rate) *
              100,
            returnPerYear: 0,
            imgUrl: token.logo_url,
            chain: chain.chain_id
          })
        };
        })
      });

      dispatch(setTokens(newTokens));
      setTokenData(newTokens);
      handleHistorical(newTokens, evmWallet)
      
    });
  };

  //keeps user "logged in" after refresh or if reloading the page
  const handleAccountCheck = async (accounts: string[]) => {
    if (accounts.length > 0) {
      dispatch(setIsConnected(true));
      dispatch(setEVMAccount(accounts[0]));
      await handleCovalent(accounts[0]);
      //handleHistorical(accounts[0]);
      //handleHistoricalZoom();
    }
  };

  useLayoutEffect(() => {
    //handles MM disconnecting or changing accounts
    //@ts-ignore
    if (window.ethereum) {
      //@ts-ignore
      const ethereum = window.ethereum;
      const web3Provider = new ethers.providers.Web3Provider(ethereum);
      let signer = web3Provider.getSigner();
      if (ethereum) {
        //updates the redux data when user changes accounts or logs in
        ethereum.on("accountsChanged", async (accounts: any) => {
          if (accounts.length === 0) {
            console.log("disconnected!");
            dispatch(setIsConnected(false));
            dispatch(setEVMAccount(""));
          } else {
            //handle updating account information
            console.log("accounts switched");
            dispatch(setIsConnected(true));
            //@ts-ignore
            const walletAddr = await signer.getAddress();
            dispatch(setEVMAccount(walletAddr));
            handleCovalent(walletAddr).then((response) => response);
            //handleHistorical(walletAddr);
          }
        });
        ethereum.on("chainChanged", async (chainId: any) => {
          console.log("chain changed!");
          // const walletAddr = await signer.getAddress();
          // const ethersResp = await web3Provider.getNetwork();
          // console.log(walletAddr);
          // console.log(ethersResp.chainId);
          //@ts-ignore
          //handleCovalent(walletAddr, ethersResp.chainId);
        });

        ethereum.request({ method: "eth_accounts" }).then(handleAccountCheck);
      }
    }

    //sets data updates to every 5 minutes
    //only retrieves data if a wallet is connected
  }, [solAcct]);

  return null;
};

export default UserUpdater;
